import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react"

import { cn } from "~/v2/utils/cn"

const variants = {
  default: "bg-primary text-primary-foreground hover:opacity-90",
  ghost: "bg-transparent text-foreground hover:bg-muted",
  outline: "border border-border bg-background text-foreground hover:bg-muted",
} as const

const sizes = {
  default: "h-10 px-4 py-2 text-sm",
  lg: "h-11 px-5 text-base",
  sm: "h-8 px-3 text-sm",
} as const

type ButtonVariant = keyof typeof variants
type ButtonSize = keyof typeof sizes

type ButtonProps = (
  | ButtonHTMLAttributes<HTMLButtonElement>
  | HTMLAttributes<HTMLSpanElement>
) & {
  asChild?: boolean
  children?: ReactNode
  className?: string
  size?: ButtonSize
  type?: ButtonHTMLAttributes<HTMLButtonElement>["type"]
  variant?: ButtonVariant
}

const Button = ({
  asChild = false,
  children,
  className,
  size = "default",
  type = "button",
  variant = "default",
  ...props
}: ButtonProps) => {
  const classes = cn(
    "inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
    variants[variant],
    sizes[size],
    className,
  )

  if (asChild) {
    return (
      <span className={classes} {...(props as HTMLAttributes<HTMLSpanElement>)}>
        {children}
      </span>
    )
  }

  return (
    <button
      className={classes}
      type={type as ButtonHTMLAttributes<HTMLButtonElement>["type"]}
      {...(props as ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {children}
    </button>
  )
}

export default Button
